import { Injectable } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';

import * as fromStore from '../../store';

import { catchError, filter, map, mergeMap, of, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { UserProfileService } from '../../services/user-profile.service';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { MenuService } from '../../services/menu.service';
import { DashboardService } from '../../services/dashboard.service';
import { MonitoringService } from '../../services/monitoring.service';
import { Router } from '@angular/router';
import { odataParamsFactory } from '@tdw/core';
import * as Sentry from '@sentry/angular';

@Injectable()
export class AppEffects {

  constructor(
    private actions$: Actions,
    private userService: UserProfileService,
    private authService: MsalService,
    private menuService: MenuService,
    private dashboardService: DashboardService,
    private store: Store<fromStore.State>,
    private monitorService: MonitoringService,
    private router: Router
  ) { }

  trackActions$ = createEffect(() => this.actions$.pipe(
    map(action => this.monitorService.logTrace(`${action.type}`))
  ), { dispatch: false });

  loadUserProfile$ = createEffect(() => this.actions$.pipe(
    startWith(fromStore.actions.USERPROFILE.loadUserProfile),
    ofType(fromStore.actions.USERPROFILE.loadUserProfile),
    switchMap(action => this.userService.getProfile()
      .pipe(map(profile => fromStore.actions.USERPROFILE.loadUserProfileSuccess({ profile })),
        catchError(error => of(fromStore.actions.USERPROFILE.loadUserProfileFailure({ error })))))));

  navToReturnUrlOnUserProfileLoad$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadUserProfileSuccess),
    withLatestFrom(this.store.select(fromStore.selectors.ROUTER.selectQueryParam('returnUrl'))),
    filter((action, returnUrl) => !!returnUrl),
    tap(([action, url]) => this.router.navigateByUrl(url || '/'))
  ), { dispatch: false });

  loadUserProfileMonitor$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadUserProfileSuccess),
    tap(action => {
      Sentry.setUser({
        id: action.profile.username,
        email: action.profile.claims.emailaddress,
        username: action.profile.claims.preferred_username
      });
      this.monitorService.appInsights.setAuthenticatedUserContext(action.profile.username);
      this.monitorService.logTrace(`[PROFILE LOAD]::${action.profile.username}`,
        {
          'authenticated': action.profile.isAuthenticated,
          'username': action.profile.username,
          'authType': action.profile.authType,
          'claims': action.profile.claims,
          'companyAccesses': action.profile.companyAccesses
        });
    })),
    { dispatch: false });

  logoutUser$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.logout),
    switchMap(action => this.authService.logout()
      .pipe(
        tap(_ => this.monitorService.appInsights.clearAuthenticatedUserContext()),
        map(_ => fromStore.actions.USERPROFILE.logoutSuccess()),
        catchError(error => of(fromStore.actions.USERPROFILE.logoutFail({ error })))
      ))));

  logoutMonitor$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.logoutSuccess),
    tap(action => this.monitorService.logEvent(`[PROFILE LOGOUT]`))
  ), { dispatch: false });

  menuItems$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadUserProfileSuccess),
    withLatestFrom(this.store.select(fromStore.selectors.USERPROFILE.selectUserProfile)),
    switchMap(([action, profile]) => this.menuService.getMenuItems()
      .pipe(map(menuItems => fromStore.actions.USERPROFILE.loadMenuSuccess({ menuItems })),
        catchError(error => of(fromStore.actions.USERPROFILE.loadMenuFailure({ error })))
      ))));

  dashboardItems$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadUserProfileSuccess),
    withLatestFrom(this.store.select(fromStore.selectors.USERPROFILE.selectUserProfile)),
    switchMap(([action, profile]) => this.dashboardService.getDashboardItems()
      .pipe(map(dashboardItems => fromStore.actions.USERPROFILE.loadDashboardSuccess({ dashboardItems })),
        catchError(error => of(fromStore.actions.USERPROFILE.loadDashboardFailure({ error })))
      ))));

  monitorErrors$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.USERPROFILE.logoutFail,
      fromStore.actions.USERPROFILE.loadMenuFailure,
      fromStore.actions.USERPROFILE.loadUserProfileFailure),
    tap(error => this.monitorService.logException(error.error)),
    map(error => fromStore.actions.ERRORS.addGlobalError({ error: error.error }))
  ));

  loadConfigAfterUserLoads$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadUserProfileSuccess),
    map(action => fromStore.actions.USERPROFILE.loadAppConfig())
  ));

  loadConfig$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.USERPROFILE.loadAppConfig),
    switchMap(action => this.userService.getAppConfig(odataParamsFactory({}))
      .pipe(map(config => fromStore.actions.USERPROFILE.loadAppConfigSuccess({ config })),
        catchError(error => of(fromStore.actions.USERPROFILE.loadAppConfigFail({ error })))))));


}
